import { Box, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { useZipCodeObjectData } from 'data2/useZipCodeObjectData';
import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cacheSelectors } from 'redux/cache.slice';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import { WsFormCheckbox } from 'stories/forms/WsFormCheckbox';
import { WsFormRadioGroup } from 'stories/forms/WsFormRadioGroup';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import WsFlexbox from 'stories/layout/WsFlexbox';
import * as yup from 'yup';

import { StepProps } from './CreateListing';
import { ListingFormTextField, ListingRow } from './ListingComponents';
import StepperFooter from './StepperFooter';

interface FormValues {
  useEmail: boolean;
  usePhone: boolean;
  email: string;
  phone: string;
  price: string;
  zipCode: string;
  showContactInfo: string;
}

const validationSchema = yup.object().shape({
  useEmail: yup.boolean(),
  usePhone: yup.boolean(),
  showContactInfo: yup.string(),
  email: yup.string().when(['useEmail', 'showContactInfo'], {
    is: (useEmail: any, showContactInfo: any) => {
      return useEmail === true && showContactInfo === '1';
    },
    then: formValidationSchema.email,
    otherwise: yup.string(),
  }),
  phone: yup.string().when(['usePhone', 'showContactInfo'], {
    is: (usePhone: any, showContactInfo: any) => {
      return usePhone === true && showContactInfo === '1';
    },
    then: formValidationSchema.phone,
    otherwise: yup.string(),
  }),
  price: formValidationSchema.required,
  zipCode: formValidationSchema.zipCode,
});

const ListingCarInfo = (props: StepProps) => {
  const dispatch = useDispatch();

  const { findOne: findZCO } = useZipCodeObjectData();

  const [runOnce, runOnceSet] = React.useState(true);

  const geoLocation = useSelector((state: RootState) => newListingSelectors.geoLocation(state));
  const price = useSelector((state: RootState) => newListingSelectors.price(state));
  const zipCode = useSelector((state: RootState) => newListingSelectors.zipCode(state));
  const city = useSelector((state: RootState) => newListingSelectors.city(state));
  const state = useSelector((state: RootState) => newListingSelectors.state(state));
  const useEmail = useSelector((state: RootState) => newListingSelectors.useEmail(state));
  const usePhone = useSelector((state: RootState) => newListingSelectors.usePhone(state));
  const email = useSelector((state: RootState) => newListingSelectors.email(state));
  const phone = useSelector((state: RootState) => newListingSelectors.phone(state));
  const showContactInfo = useSelector((state: RootState) => newListingSelectors.showContactInfo(state));
  const user = useSelector((state: RootState) => cacheSelectors.user(state));
  const [initEmail, initEmailTo] = React.useState(email);

  useEffect(() => {
    if (runOnce) {
      runOnceSet(false);
      props.setValidFn(true);
      if (!email.length) {
        dispatch(newListingActions.email(user!.email));
        initEmailTo(user!.email);
      }
      if (!useEmail) dispatch(newListingActions.useEmail(true));
    }
  }, [runOnce, props, dispatch, user, email, useEmail]);

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      // console.log('values', formValues);
      if (showContactInfo) {
        if (!useEmail && email) {
          dispatch(newListingActions.email(''));
        }
        if (!usePhone && phone) {
          dispatch(newListingActions.phone(''));
        }
      } else {
        if (usePhone) dispatch(newListingActions.usePhone(false));
        if (phone.length) dispatch(newListingActions.phone(''));
      }
      props.handleNext();
    } catch (error: any) {
      setSubmitting(false);
    }
  };

  const validateForm = async (values: FormValues) => {
    // console.log('car info email', validationSchema.fields.zipCode.isValidSync(values.email));
    const errors: any = {};
    if (values.zipCode !== zipCode) {
      if (validationSchema.fields.zipCode.isValidSync(values.zipCode)) {
        const zo = await findZCO(values.zipCode);
        if (zo) {
          dispatch(newListingActions.zipCodeObject(zo));
        } else {
          errors.zipCode = 'Not found';
          if (geoLocation) {
            dispatch(newListingActions.zipCodeObject(undefined));
          }
        }
      } else if (geoLocation) {
        dispatch(newListingActions.zipCodeObject(undefined));
      }
    }
    if (values.showContactInfo === '1') {
      if (values.useEmail || values.usePhone) {
        if (values.useEmail && !values.email) errors.email = 'Required';
        if (values.usePhone && !values.phone) errors.phone = 'Required';
      } else if ((!values.useEmail || !values.email) && (!values.usePhone || !values.phone)) {
        errors.email = 'Email or phone required';
        errors.phone = 'Email or phone required';
      }
    }
    return errors;
  };

  const onValueChange = async (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
    formik?: FormikProps<any>,
  ) => {
    formik?.handleChange(event);
    // console.log('change', { id: event.target.id, value: event.target.value });
    switch (event.target.id as keyof FormValues) {
      case 'useEmail': {
        dispatch(newListingActions.useEmail((event as React.ChangeEvent<HTMLInputElement>).target.checked));
        break;
      }
      case 'usePhone': {
        dispatch(newListingActions.usePhone((event as React.ChangeEvent<HTMLInputElement>).target.checked));
        break;
      }
      case 'email': {
        dispatch(newListingActions.email(event.target.value || ''));
        break;
      }
      case 'phone': {
        dispatch(newListingActions.phone(event.target.value || ''));
        break;
      }
      case 'price': {
        dispatch(newListingActions.price(+event.target.value));
        break;
      }
      case 'zipCode': {
        dispatch(newListingActions.zipCode(event.target.value));
        break;
      }
      case 'showContactInfo': {
        dispatch(newListingActions.showContactInfo(event.target.value === '1' ? true : false));
        break;
      }
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      validate={validateForm}
      enableReinitialize={true}
      initialValues={{
        useEmail: true,
        usePhone,
        email: initEmail,
        phone: phone || '',
        price: price === 0 ? '' : price.toString(),
        zipCode: zipCode,
        showContactInfo: showContactInfo ? '1' : '0',
      }}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {formik => (
        <>
          <Grid container component="form" onSubmit={formik.handleSubmit}>
            <ListingRow label="Price" gridLR={[3, 9]}>
              <ListingFormTextField
                id="price"
                currency
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                formik={formik}
              />
            </ListingRow>
            <ListingRow label="Zip Code" gridLR={[3, 9]}>
              <WsFlexbox padding={0} gap={2}>
                <ListingFormTextField
                  id="zipCode"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                  formik={formik}
                />
                {city && city.length > 0 && (
                  <Typography variant="body1" color="primary">{`${city}, ${state}`}</Typography>
                )}
              </WsFlexbox>
            </ListingRow>
            <ListingRow label="Contact Info" gridLR={[3, 9]}>
              <Grid container>
                <Grid item xs={12}>
                  <WsFormRadioGroup
                    id="showContactInfo"
                    formik={formik}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue('showContactInfo', event.target.value, true);
                      onValueChange(
                        {
                          ...event,
                          target: {
                            ...event.target,
                            id: 'showContactInfo',
                            value: event.target.value,
                          },
                        },
                        formik,
                      );
                    }}
                  >
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="Hide your contact info. We will forward all inquiries to your email, protecting your private information."
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Show the following contact info in the listing:"
                    />
                  </WsFormRadioGroup>
                </Grid>
                <Grid item xs={1}></Grid>
                <Grid item xs={11}>
                  <WsFormCheckbox
                    formik={formik}
                    id="useEmail"
                    label="Email"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                    disabled={true}
                  ></WsFormCheckbox>
                  <WsFormTextField
                    id="email"
                    type="email"
                    size="small"
                    label=""
                    readOnly={true}
                    width={240}
                    sx={{
                      position: 'relative',
                      top: '12px',
                    }}
                    helperText="Read only"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                    formik={formik}
                    disabled={formik.values.showContactInfo === '0' || !formik.values.useEmail}
                  />
                </Grid>
                <Grid item xs={1}></Grid>
                <Grid item xs={11}>
                  <WsFormCheckbox
                    formik={formik}
                    id="usePhone"
                    label="Phone"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                    disabled={formik.values.showContactInfo === '0'}
                  ></WsFormCheckbox>
                  <WsFormTextField
                    id="phone"
                    type="phone"
                    phone
                    size="small"
                    label=""
                    width={180}
                    sx={{
                      position: 'relative',
                      top: '12px',
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                    formik={formik}
                    disabled={formik.values.showContactInfo === '0' || !formik.values.usePhone}
                  />
                </Grid>
              </Grid>
            </ListingRow>
            <StepperFooter
              handleCancel={props.handleCancel}
              isFirst={props.isFirst}
              isLast={props.isLast}
              handleBack={props.handleBack}
            />
          </Grid>
        </>
      )}
    </Formik>
  );
};

export default ListingCarInfo;
